import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SectionEditor from '../components/SectionEditor';
import TextEditor from '../components/TextEditor';
import { researchService, Report } from '../services/api';
import '../styles/report.css';

const ReportView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!id) return;
        const data = await researchService.getReport(id);
        console.log('Report data:', JSON.stringify(data, null, 2));
        
        // Ensure the report has a valid structure for rendering
        let processedData = { ...data };
        
        // Make sure we have an outline object
        if (!processedData.outline) {
          console.log('Creating missing outline object');
          processedData.outline = { sections: [] };
        }
        
        // Make sure we have a sections array
        if (!processedData.outline.sections) {
          console.log('Creating missing sections array');
          processedData.outline.sections = [];
        }
        
        // If we have content but no sections, try to create sections from the content
        if (processedData.content && processedData.outline.sections.length === 0) {
          console.log('Creating sections from full content');
          // Create at least one section if none exist
          processedData.outline.sections = [
            {
              id: '0',
              title: 'Main Content',
              content: processedData.content
            }
          ];
        }
        
        // If sections exist but have no content, try to extract content
        if (processedData.outline.sections.length > 0 && processedData.content) {
          const sectionsWithoutContent = processedData.outline.sections.filter(s => !s.content);
          
          if (sectionsWithoutContent.length > 0) {
            console.log('Some sections are missing content, attempting to extract from full content');
            
            // Try to extract content for each section based on titles
            let remainingContent = processedData.content;
            processedData.outline.sections = processedData.outline.sections.map((section) => {
              // If section already has content, keep it
              if (section.content) {
                return section;
              }
              
              // Try to find section title in content
              if (section.title) {
                const titleIndex = remainingContent.indexOf(section.title);
                if (titleIndex !== -1) {
                  // Skip the title and get content until next section
                  const startIndex = titleIndex + section.title.length;
                  
                  // Find the next section title in the content
                  const nextSectionIndex = processedData.outline?.sections
                    .filter(s => s.title && s.title !== section.title)
                    .reduce((minIndex, nextSection) => {
                      const idx = remainingContent.indexOf(nextSection.title, startIndex);
                      return idx !== -1 && (idx < minIndex || minIndex === -1) ? idx : minIndex;
                    }, -1);
                  
                  const endIndex = nextSectionIndex !== -1 ? nextSectionIndex : remainingContent.length;
                  
                  // Extract section content
                  section.content = remainingContent.substring(startIndex, endIndex).trim();
                  console.log(`Extracted content for section '${section.title}':`, 
                    section.content?.substring(0, 100) + (section.content?.length > 100 ? '...' : ''));
                  
                  // Update remaining content
                  if (nextSectionIndex !== -1) {
                    remainingContent = remainingContent.substring(nextSectionIndex !== undefined ? nextSectionIndex : 0);
                  } else {
                    remainingContent = '';
                  }
                } else {
                  // If title not found, assign a portion of the content
                  const contentChunkSize = Math.floor((processedData.content?.length || 0) / Math.max((processedData.outline?.sections.length || 1), 1));
                  const startIdx = (processedData.outline?.sections.indexOf(section) || 0) * contentChunkSize;
                  const endIdx = startIdx + contentChunkSize;
                  section.content = processedData.content?.substring(startIdx, endIdx).trim() || '';
                }
              } else {
                // If no title, create a generic one and assign some content
                section.title = `Section ${(processedData.outline?.sections.indexOf(section) || 0) + 1}`;
                const contentChunkSize = Math.floor((processedData.content?.length || 0) / Math.max((processedData.outline?.sections.length || 1), 1));
                const startIdx = (processedData.outline?.sections.indexOf(section) || 0) * contentChunkSize;
                const endIdx = startIdx + contentChunkSize;
                section.content = processedData.content?.substring(startIdx, endIdx).trim() || '';
              }
              
              return section;
            });
          }
        }
        
        // Ensure each section has at least an empty string for content
        if (processedData.outline?.sections) {
          processedData.outline.sections = processedData.outline.sections.map(section => ({
            ...section,
            content: section.content || '',
            title: section.title || `Section ${(processedData.outline?.sections.indexOf(section) || 0) + 1}`
          }));
        }
        
        console.log('Processed report data:', processedData);
        setReport(processedData);
      } catch (err: any) {
        console.error('Error fetching report:', err);
        setError(err.message || 'Failed to fetch report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handleDelete = async () => {
    if (!report) return;
    
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        await researchService.deleteReport(report.id);
        navigate('/reports');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to delete report');
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Report not found'}
        </div>
        <button
          onClick={() => navigate('/reports')}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Back to Reports
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        {/* Report Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-700 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <div className="mb-2">
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold tracking-tight">
                    {report.outline?.title || `Research on ${report.topic}`}
                  </h1>
                  <button 
                    onClick={() => {
                      const newTitle = prompt('Edit report title:', report.outline?.title || `Research on ${report.topic}`);
                      if (newTitle && newTitle.trim()) {
                        researchService.updateReportTitle(report.id, newTitle).then(() => {
                          const updated = { ...report };
                          if (!updated.outline) updated.outline = { sections: [] };
                          updated.outline.title = newTitle;
                          setReport(updated);
                        }).catch(err => {
                          setError(err.response?.data?.detail || 'Failed to update title');
                        });
                      }
                    }} 
                    className="ml-2 text-primary-200 hover:text-white focus:outline-none"
                    title="Edit title"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-primary-100">
                Topic: {report.topic}
              </p>
              <p className="text-primary-100 text-sm mt-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Created: {new Date(report.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/reports')}
                className="flex items-center bg-white text-primary-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all duration-200 shadow-sm border border-primary-200 focus:ring-2 focus:ring-primary-200 focus:outline-none"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Reports
              </button>
              <button
                onClick={() => {
                  // Create a blob from the report content
                  const reportTitle = report.outline?.title || `Research on ${report.topic}`;
                  const fileName = `${reportTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
                  const blob = new Blob([report.content || ''], { type: 'text/markdown' });
                  const url = URL.createObjectURL(blob);
                  
                  // Create a temporary link and trigger download
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = fileName;
                  document.body.appendChild(a);
                  a.click();
                  
                  // Clean up
                  setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }, 100);
                }}
                className="flex items-center bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-all duration-200 shadow-sm focus:ring-2 focus:ring-emerald-300 focus:outline-none"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Report
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-all duration-200 shadow-sm focus:ring-2 focus:ring-red-300 focus:outline-none"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
        
        {/* Report Summary */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold mb-3 text-gray-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Summary
          </h2>
          <TextEditor 
            content={report.summary || ''}
            label="Summary"
            onSave={async (content) => {
              try {
                await researchService.updateReportSummary(report.id, content);
                const updated = { ...report };
                updated.summary = content;
                setReport(updated);
              } catch (err: any) {
                setError(err.response?.data?.detail || 'Failed to update summary');
              }
            }}
          />
        </div>
        
        {/* Report Content */}
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Full Report
          </h2>
          
          {/* Introduction Section */}
          {report.outline?.introduction !== undefined && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Introduction</h3>
              <TextEditor 
                content={report.outline.introduction || ''}
                label="Introduction"
                onSave={async (content) => {
                  try {
                    await researchService.updateReportIntroduction(report.id, content);
                    const updated = { ...report };
                    if (!updated.outline) updated.outline = { sections: [] };
                    updated.outline.introduction = content;
                    setReport(updated);
                  } catch (err: any) {
                    setError(err.response?.data?.detail || 'Failed to update introduction');
                  }
                }}
              />
            </div>
          )}
          <div className="report-content prose max-w-none prose-headings:text-primary-800 prose-a:text-primary-600 prose-strong:text-gray-800 prose-blockquote:border-l-primary-500 prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-4">
            {report.outline?.sections?.length ? (
              report.outline.sections.map((section, idx) => {
                console.log(`Rendering section ${idx}:`, section);
                return (
                <SectionEditor
                  key={section.title + idx}
                  section={{ 
                    id: idx.toString(), 
                    title: section.title || `Section ${idx + 1}`, 
                    content: section.content || '' 
                  }}
                  onSave={async (content, title) => {
                    // Call API to update
                    try {
                      await researchService.updateSection(report.id, idx, content, title);
                      const updated = { ...report };
                      if (updated.outline && updated.outline.sections) {
                        updated.outline.sections[idx].content = content;
                        if (title) {
                          updated.outline.sections[idx].title = title;
                        }
                        setReport(updated);
                      }
                    } catch (err: any) {
                      setError(err.response?.data?.detail || 'Failed to save section');
                    }
                  }}
                  onRewrite={async (content) => {
                    // Call API to rewrite
                    try {
                      console.log('Before rewrite - Current report:', JSON.stringify(report));
                      const rewritten = await researchService.rewriteSection(report.id, idx, content);
                      console.log('Rewrite API response:', rewritten);
                      
                      // Create a proper deep copy of the report object
                      const updated = JSON.parse(JSON.stringify(report));
                      
                      // Update only the specific section content
                      if (updated.outline && updated.outline.sections && 
                          Array.isArray(updated.outline.sections) && 
                          updated.outline.sections.length > idx) {
                        
                        console.log(`Updating section ${idx} content with rewritten text`);
                        updated.outline.sections[idx].content = rewritten.content;
                        
                        // Preserve any other report properties
                        console.log('Updated report object:', JSON.stringify(updated));
                        setReport(updated);
                      } else {
                        console.error('Cannot update report - invalid structure:', updated);
                      }
                      
                      // Return the rewritten content in the expected format
                      return rewritten;
                    } catch (err: any) {
                      console.error('Error during rewrite:', err);
                      setError(err.response?.data?.detail || 'Failed to rewrite section');
                      // Return a default object to satisfy the type requirement
                      return { content: content };
                    }
                  }}
                />
                );
              })
            ) : (
              <p>No content available</p>
            )}
          </div>
        </div>
        
        {/* Report Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Report ID: {report.id}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Generated by Research Agent
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportView;
